import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  lazy,
  Suspense,
} from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { useAuditContext } from "../../contexts/AuditContext";
import { toast } from "react-toastify";

// Lazy load InvoicePreviewModal - only loaded when invoice enhancements are enabled
const InvoicePreviewModal = lazy(() => import("./InvoicePreviewModal"));

// Session Monitor Component (development only)
const SessionMonitor = () => {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await secureAxios.get("/api/auth/validate-session");
        setSessionInfo({
          valid: response.data.sessionValid,
          expiresAt: response.data.sessionInfo?.expiresAt,
          lastSeen: response.data.sessionInfo?.lastSeen,
          timeRemaining: response.data.sessionInfo?.expiresAt
            ? Math.max(
                0,
                new Date(response.data.sessionInfo.expiresAt) - Date.now()
              )
            : 0,
        });
      } catch (error) {
        setSessionInfo({ valid: false, error: error.message });
      }
    };

    const interval = setInterval(checkSession, 30000);
    checkSession();
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleActivity = () => setLastActivity(Date.now());

    ["click", "keydown", "mousemove", "scroll"].forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      ["click", "keydown", "mousemove", "scroll"].forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  if (process.env.NODE_ENV !== "development") return null;

  return (
    <div
      className="alert alert-warning position-fixed d-none d-lg-block"
      style={{
        top: "10px",
        right: "10px",
        zIndex: 9999,
        fontSize: "11px",
        maxWidth: "280px",
        opacity: 0.9,
        border: "1px solid #ffc107",
      }}
    >
      <div>
        <strong>Session Debug Monitor</strong>
      </div>
      <div>Valid: {sessionInfo?.valid ? "YES" : "NO"}</div>
      <div>
        Time Left:{" "}
        {sessionInfo?.timeRemaining
          ? Math.floor(sessionInfo.timeRemaining / 1000 / 60) +
            "m " +
            Math.floor((sessionInfo.timeRemaining / 1000) % 60) +
            "s"
          : "Unknown"}
      </div>
      <div>
        Last Activity: {Math.floor((Date.now() - lastActivity) / 1000)}s ago
      </div>
      <div>
        Expires:{" "}
        {sessionInfo?.expiresAt
          ? new Date(sessionInfo.expiresAt).toLocaleTimeString()
          : "Unknown"}
      </div>
    </div>
  );
};

// helper function to avoid timezone issues
const extractDateParts = (dateString) => {
  if (!dateString) return { month: null, year: null };

  // Handle ISO date strings (YYYY-MM-DD) by parsing them manually
  // to avoid timezone issues
  if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const datePart = dateString.split("T")[0]; // Get just the date part
    const [year, month, day] = datePart.split("-");
    return {
      month: parseInt(month, 10),
      year: parseInt(year, 10),
    };
  }

  // Fallback to Date object for other formats
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return { month: null, year: null };

    return {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  } catch (error) {
    return { month: null, year: null };
  }
};

// Format rate (preserves up to 3 decimals when needed, e.g. $0.725)
const formatRate = (amount) => {
  const num = parseFloat(amount) || 0;
  const decimalPart = num.toString().split(".")[1] || "";
  const decimals = Math.min(Math.max(decimalPart.length, 2), 3);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

// Float-safe rounding to 2 decimal places — applied only to final totals, not per-line
// Uses mills (1/1000) to avoid JS float precision errors (e.g. 67 × $0.725 = 48.574999...)
const roundAmount = (val) => Math.round(Math.round(val * 1000) / 10) / 100;

// Milestone Selector Component for Flat Rate Services
const MilestoneSelector = ({
  authorizationId,
  milestonesCache,
  loadMilestones,
  selectedMilestones,
  setSelectedMilestones,
  rate,
}) => {
  const [loading, setLoading] = useState(false);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    const fetchMilestones = async () => {
      if (!authorizationId) return;
      
      // Check cache first
      if (milestonesCache.has(authorizationId)) {
        setMilestones(milestonesCache.get(authorizationId));
        return;
      }

      setLoading(true);
      try {
        const data = await loadMilestones(authorizationId);
        setMilestones(data);
      } finally {
        setLoading(false);
      }
    };

    fetchMilestones();
  }, [authorizationId, milestonesCache, loadMilestones]);

  const availableMilestones = milestones.filter(m => !m.is_invoiced);
  const invoicedMilestones = milestones.filter(m => m.is_invoiced);
  const selectedMilestoneId = selectedMilestones[authorizationId];

  if (loading) {
    return (
      <div className="mb-3 p-2 bg-light rounded">
        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
        Loading milestones...
      </div>
    );
  }

  if (milestones.length === 0) {
    return (
      <div className="alert alert-warning py-2 small mb-2">
        <i className="bi bi-exclamation-triangle me-1"></i>
        No milestones defined for this authorization. Please add milestones before invoicing.
      </div>
    );
  }

  if (availableMilestones.length === 0) {
    return (
      <div className="alert alert-success py-2 small mb-2">
        <i className="bi bi-check-circle me-1"></i>
        All milestones have been invoiced ({invoicedMilestones.length}/{milestones.length}).
      </div>
    );
  }

  return (
    <div className="mb-3 p-3 bg-light rounded border">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>
          <i className="bi bi-list-check me-1"></i>
          Select Milestone to Invoice
        </strong>
        <span className="badge bg-info">
          {availableMilestones.length} of {milestones.length} available
        </span>
      </div>
      
      <div className="row g-2">
        {milestones.map((milestone) => (
          <div key={milestone.milestone_id} className="col-md-6 col-lg-4">
            <div
              className={`card h-100 ${
                milestone.is_invoiced
                  ? "bg-secondary text-white opacity-50"
                  : selectedMilestoneId === milestone.milestone_id
                  ? "border-primary bg-primary bg-opacity-10"
                  : "border-light"
              }`}
              style={{
                cursor: milestone.is_invoiced ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (!milestone.is_invoiced) {
                  setSelectedMilestones((prev) => ({
                    ...prev,
                    [authorizationId]: milestone.milestone_id,
                  }));
                }
              }}
            >
              <div className="card-body py-2 px-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      checked={selectedMilestoneId === milestone.milestone_id}
                      disabled={milestone.is_invoiced}
                      onChange={() => {}}
                    />
                    <span className={milestone.is_invoiced ? "text-decoration-line-through" : ""}>
                      {milestone.milestone_name}
                    </span>
                  </div>
                  {milestone.is_invoiced ? (
                    <span className="badge bg-success">
                      <i className="bi bi-lock-fill me-1"></i>Invoiced
                    </span>
                  ) : (
                    <span className="badge bg-warning text-dark">
                      {formatRate(rate)}
                    </span>
                  )}
                </div>
                {milestone.description && (
                  <small className="text-muted d-block mt-1">
                    {milestone.description}
                  </small>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {!selectedMilestoneId && availableMilestones.length > 0 && (
        <div className="text-danger small mt-2">
          <i className="bi bi-exclamation-circle me-1"></i>
          Please select a milestone to invoice
        </div>
      )}
    </div>
  );
};

const InvoiceGeneratorPage = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    consumer_id: "",
    user_id: "",
    auth_number: "",
  });

  const [consumers, setConsumers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState({
    job_logs: [],
    mileage_logs: [],
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [monthlyReportStatuses, setMonthlyReportStatuses] = useState({});
  const [hasIncompleteReports, setHasIncompleteReports] = useState(false);

  // Enhanced functionality state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [detailsCache, setDetailsCache] = useState(new Map());
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Milestone state for flat rate invoicing
  const [milestonesCache, setMilestonesCache] = useState(new Map());
  const [selectedMilestones, setSelectedMilestones] = useState({}); // { authorization_id: milestone_id }

  // Email functionality state
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailToValue, setEmailToValue] = useState("");
  const [emailSubjectValue, setEmailSubjectValue] = useState("");
  const [emailBodyValue, setEmailBodyValue] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Add pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
  });

  // NEW: Individual invoice preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewInvoiceData, setPreviewInvoiceData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // NEW: Generation mode toggle (batch vs individual)
  const [generationMode, setGenerationMode] = useState("batch"); // "batch" or "individual"

  // Mobile: Collapsible filters state
  const [showFilters, setShowFilters] = useState(false);

  const { tenant_id, user, loadingUser } = useContext(AuthContext);
  const { logAuditEvent, PURPOSES } = useAuditContext();
  const user_id = user?.user_id;
  const apiBase = getApiBaseURL();

  // Check if invoice enhancements are enabled for this tenant
  const invoiceEnhancementsEnabled = user?.enable_invoices === true;

  // Force batch mode if enhancements not enabled (performance optimization)
  useEffect(() => {
    if (!invoiceEnhancementsEnabled && generationMode !== "batch") {
      setGenerationMode("batch");
    }
  }, [invoiceEnhancementsEnabled, generationMode]);

  // Fetch monthly report status for authorizations - with chunking for large datasets
  const fetchMonthlyReportStatuses = useCallback(
    async (previewDataObj) => {
      if (!tenant_id || !previewDataObj) {
        console.log("fetchMonthlyReportStatuses: skipping - no tenant_id or previewDataObj");
        return;
      }

      try {
        // Get authorization data with proper deduplication
        const authDataMap = new Map();
        [
          ...(Array.isArray(previewDataObj.job_logs)
            ? previewDataObj.job_logs
            : []),
          ...(Array.isArray(previewDataObj.mileage_logs)
            ? previewDataObj.mileage_logs
            : []),
        ].forEach((log) => {
          if (log.authorization_number) {
            const logDate = log.date_of_service || log.trip_date;
            if (logDate) {
              // Use the fixed date extraction function
              const { month, year } = extractDateParts(logDate);
              if (month && year) {
                const key = `${log.authorization_number}_${month}_${year}`;
                if (!authDataMap.has(key)) {
                  authDataMap.set(key, {
                    authorization_number: log.authorization_number,
                    month: month,
                    year: year,
                  });
                }
              }
            }
          }
        });

        const authorizations = Array.from(authDataMap.values());

        // Skip API call if no authorizations found
        if (!authorizations || authorizations.length === 0) {
          console.log("fetchMonthlyReportStatuses: no authorizations to check, skipping API call");
          setMonthlyReportStatuses({});
          setHasIncompleteReports(false);
          return;
        }

        console.log(`fetchMonthlyReportStatuses: checking ${authorizations.length} authorizations`);

        // Split into chunks of 100 to stay within server limits
        const CHUNK_SIZE = 100;
        const chunks = [];
        for (let i = 0; i < authorizations.length; i += CHUNK_SIZE) {
          chunks.push(authorizations.slice(i, i + CHUNK_SIZE));
        }

        console.log(`fetchMonthlyReportStatuses: splitting into ${chunks.length} batch request(s)`);

        // Make parallel requests for each chunk
        const results = await Promise.allSettled(
          chunks.map((chunk) =>
            secureAxios.post(`${apiBase}/api/reports/batch-status`, {
              tenant_id,
              authorizations: chunk,
            })
          )
        );

        // Merge all statuses from successful responses
        const mergedStatuses = {};
        let totalFound = 0;

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            Object.assign(mergedStatuses, result.value.data.statuses || {});
            totalFound += result.value.data.total_found || 0;
          } else {
            console.warn(`Batch ${index + 1} failed:`, result.reason?.message);
          }
        });

        // Set the merged statuses
        setMonthlyReportStatuses(mergedStatuses);

        // Check for incomplete reports
        const incomplete = Object.values(mergedStatuses).some(
          (status) => !status.hasMatchingReport || status.status !== "Completed"
        );
        setHasIncompleteReports(incomplete);

        console.log(
          `Batch loaded ${Object.keys(mergedStatuses).length} monthly report statuses in ${chunks.length} API call(s)`
        );
      } catch (err) {
        console.error("Failed to fetch batch monthly report statuses", err);
        console.error("Batch status error details:", {
          status: err.response?.status,
          data: err.response?.data,
          tenant_id_was: tenant_id,
        });
        // Fallback to empty statuses instead of failing completely
        setMonthlyReportStatuses({});
        setHasIncompleteReports(false);
      }
    },
    [tenant_id, apiBase]
  );

  // CORRECTED: Single initial load function using Promise.allSettled to prevent one failure from breaking everything
  const performInitialLoad = useCallback(async () => {
    if (initialLoadComplete || loadingUser || !tenant_id || !user?.user_id)
      return;

    console.log("Starting initial load...");
    setPreviewLoading(true);

    try {
      // Set audit headers
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        "Initial invoice page load";
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      // Use Promise.allSettled so one failure doesn't break everything
      // This is important because consumers endpoint requires PHI acknowledgment
      // but preview data should still load even if consumers fails
      const [previewRes, consumersRes, usersRes] = await Promise.allSettled([
        // Load preview data
        secureAxios.get(`${apiBase}/api/invoices/preview`, {
          params: { tenant_id },
        }),
        // Load consumers (may fail due to PHI requirements)
        secureAxios.get(`${apiBase}/api/consumers`, {
          params: {
            tenant_id,
            user_id: user.user_id,
            role: user.role,
            tenant_type: user.tenant_type,
          },
        }),
        // Load users/coaches
        secureAxios.get(`${apiBase}/api/users`, {
          params: { tenant_id },
        }),
      ]);

      // Handle preview data (critical - this is the main data we need)
      const newPreviewData = {
        job_logs:
          previewRes.status === "fulfilled" &&
          Array.isArray(previewRes.value.data.job_logs)
            ? previewRes.value.data.job_logs
            : [],
        mileage_logs:
          previewRes.status === "fulfilled" &&
          Array.isArray(previewRes.value.data.mileage_logs)
            ? previewRes.value.data.mileage_logs
            : [],
      };

      // Log if preview failed
      if (previewRes.status === "rejected") {
        console.error("Preview data load failed:", previewRes.reason?.message);
      }

      // Handle consumers (may fail due to PHI requirements - that's OK)
      if (consumersRes.status === "fulfilled") {
        setConsumers(
          Array.isArray(consumersRes.value.data) ? consumersRes.value.data : []
        );
      } else {
        console.warn(
          "Consumers load failed (may require PHI acknowledgment):",
          consumersRes.reason?.message
        );
        setConsumers([]);
        // Don't show error toast - this is expected if PHI not acknowledged
      }

      // Handle coaches/users
      if (usersRes.status === "fulfilled") {
        setCoaches(
          Array.isArray(usersRes.value.data) ? usersRes.value.data : []
        );
      } else {
        console.warn("Users load failed:", usersRes.reason?.message);
        setCoaches([]);
      }

      // Always set preview data (even if empty)
      setPreviewData(newPreviewData);

      // Fetch monthly report statuses using the preview data
      if (
        newPreviewData.job_logs.length > 0 ||
        newPreviewData.mileage_logs.length > 0
      ) {
        await fetchMonthlyReportStatuses(newPreviewData);
      }

      // Log the initial load
      await logAuditEvent({
        action: "INITIAL_INVOICE_PAGE_LOAD",
        entity_type: "INVOICE",
        entity_id: null,
        entity_identifier: "Initial page load with preview data",
        contains_phi: true,
        phi_types: ["consumer_name", "authorization_number", "service_details"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });

      setInitialLoadComplete(true);
      console.log("Initial load completed successfully", {
        previewStatus: previewRes.status,
        consumersStatus: consumersRes.status,
        usersStatus: usersRes.status,
        jobLogsCount: newPreviewData.job_logs.length,
        mileageLogsCount: newPreviewData.mileage_logs.length,
      });
    } catch (err) {
      // This catch should rarely be hit now since we use allSettled
      console.error("Failed to perform initial load", err);
      setPreviewData({ job_logs: [], mileage_logs: [] });
      setConsumers([]);
      setCoaches([]);

      await logAuditEvent({
        action: "INITIAL_INVOICE_PAGE_LOAD_FAILED",
        entity_type: "INVOICE",
        entity_id: null,
        action_status: "FAILED",
        action_reason: err.message,
      });
    } finally {
      setPreviewLoading(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  }, [
    initialLoadComplete,
    loadingUser,
    tenant_id,
    user?.user_id,
    user?.role,
    user?.tenant_type,
    apiBase,
    fetchMonthlyReportStatuses,
    logAuditEvent,
    PURPOSES.PAYMENT,
  ]);

  // Single useEffect for initial load only
  useEffect(() => {
    performInitialLoad();
  }, [performInitialLoad]);

  // CORRECTED: Separate function for filtered preview (only when filters change)
  const fetchFilteredPreview = useCallback(async () => {
    if (!tenant_id || !initialLoadComplete) return;

    setPreviewLoading(true);

    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        "Load filtered invoice preview";
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      const params = { tenant_id, ...filters };
      const response = await secureAxios.get(
        `${apiBase}/api/invoices/preview`,
        { params }
      );

      if (response.data) {
        const newPreviewData = {
          job_logs: Array.isArray(response.data.job_logs)
            ? response.data.job_logs
            : [],
          mileage_logs: Array.isArray(response.data.mileage_logs)
            ? response.data.mileage_logs
            : [],
        };

        setPreviewData(newPreviewData);

        // FIXED: Also fetch monthly report statuses for filtered data
        await fetchMonthlyReportStatuses(newPreviewData);

        // Clear expanded rows when data changes
        setExpandedRows(new Set());
        setDetailsCache(new Map());
      }
    } catch (err) {
      console.error("Failed to load filtered preview:", err);
      toast.error("Failed to load preview data");
    } finally {
      setPreviewLoading(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  }, [
    tenant_id,
    filters,
    initialLoadComplete,
    apiBase,
    fetchMonthlyReportStatuses,
    PURPOSES.PAYMENT,
  ]);

  // Load detailed logs for a specific invoice group
  const loadInvoiceDetails = useCallback(
    (authorizationNumber) => {
      if (detailsCache.has(authorizationNumber)) {
        return detailsCache.get(authorizationNumber);
      }

      // Filter the existing preview data by authorization number
      const filteredJobLogs = (previewData.job_logs || []).filter(
        (log) => log.authorization_number === authorizationNumber
      );

      const filteredMileageLogs = (previewData.mileage_logs || []).filter(
        (log) => log.authorization_number === authorizationNumber
      );

      const details = {
        job_logs: filteredJobLogs,
        mileage_logs: filteredMileageLogs,
      };

      setDetailsCache((prev) =>
        new Map(prev).set(authorizationNumber, details)
      );
      return details;
    },
    [previewData, detailsCache]
  );

  // Load milestones for a flat rate authorization
  const loadMilestones = useCallback(
    async (authorizationId) => {
      if (!authorizationId || !tenant_id) return [];
      
      // Check cache first
      if (milestonesCache.has(authorizationId)) {
        return milestonesCache.get(authorizationId);
      }

      try {
        const res = await secureAxios.get(
          `/api/auth-milestones/auth/${authorizationId}`,
          { params: { tenant_id } }
        );
        const milestones = res.data || [];
        
        setMilestonesCache((prev) =>
          new Map(prev).set(authorizationId, milestones)
        );
        
        // Auto-select first available (non-invoiced) milestone if none selected
        const availableMilestone = milestones.find(m => !m.is_invoiced);
        if (availableMilestone && !selectedMilestones[authorizationId]) {
          setSelectedMilestones(prev => ({
            ...prev,
            [authorizationId]: availableMilestone.milestone_id
          }));
        }
        
        return milestones;
      } catch (err) {
        console.error("Error loading milestones:", err);
        return [];
      }
    },
    [tenant_id, milestonesCache, selectedMilestones]
  );

  // Toggle row expansion
  const toggleRowExpansion = useCallback(
    (authorizationNumber) => {
      const newExpandedRows = new Set(expandedRows);

      if (expandedRows.has(authorizationNumber)) {
        newExpandedRows.delete(authorizationNumber);
      } else {
        newExpandedRows.add(authorizationNumber);
        loadInvoiceDetails(authorizationNumber);
      }

      setExpandedRows(newExpandedRows);
    },
    [expandedRows, loadInvoiceDetails]
  );

  // Calculate summary preview with proper formatting
  const summaryPreview = Object.values(
    [
      ...(Array.isArray(previewData.job_logs) ? previewData.job_logs : []),
      ...(Array.isArray(previewData.mileage_logs)
        ? previewData.mileage_logs
        : []),
    ].reduce((acc, log) => {
      const key = log.authorization_number;
      if (!acc[key]) {
        acc[key] = {
          authorization_number: log.authorization_number,
          authorization_id: log.authorization_id,
          authorization_hours: parseFloat(log.authorization_hours) || 0,
          consumer_name: log.consumer_name,
          consumer_id: log.consumer_id,
          customer_name: log.customer_name,
          customer_id: log.customer_id,
          user_name: log.user_name,
          total_miles: 0,
          total_hours: 0,
          total_amount: 0,
          flat_rate: log.flat_rate || false,
          flat_rate_calculated: false,
          rate: parseFloat(log.applicable_rate) || parseFloat(log.rate) || 0,
          job_logs: [],
          mileage_logs: [],
        };
      }

      if (log.total_miles) {
        const miles = parseFloat(log.total_miles) || 0;
        // Use applicable_rate (date-based) if available, fallback to rate
        const rate =
          parseFloat(log.applicable_rate) || parseFloat(log.rate) || 0;
        acc[key].total_miles += miles;
        acc[key].total_amount += miles * rate;
        acc[key].mileage_logs.push(log);
      }

      if (log.hours_worked) {
        const hours = parseFloat(log.hours_worked) || 0;
        // Use applicable_rate (date-based) if available, fallback to rate
        const rate =
          parseFloat(log.applicable_rate) || parseFloat(log.rate) || 0;
        acc[key].total_hours += hours;

        if (log.flat_rate) {
          // Flat rate: use just the rate (one flat fee per invoice), not multiplied by authorization hours
          // Authorization hours represent total units available, not units to bill per invoice
          if (!acc[key].flat_rate_calculated) {
            acc[key].total_amount += rate;
            acc[key].flat_rate_calculated = true;
          }
        } else {
          // Standard: use rate x hours per log entry
          acc[key].total_amount += hours * rate;
        }
        acc[key].job_logs.push(log);
      }

      return acc;
    }, {})
  );

  // Add this function to calculate paginated data
  const getPaginatedPreview = useCallback(() => {
    if (!summaryPreview || summaryPreview.length === 0) {
      return { paginatedData: [], totalCount: 0 };
    }

    const totalCount = summaryPreview.length;
    const startIndex = (pagination.page - 1) * pagination.per_page;
    const endIndex = startIndex + pagination.per_page;
    const paginatedData = summaryPreview.slice(startIndex, endIndex);

    // Update total in pagination state if it's different
    if (pagination.total !== totalCount) {
      setPagination((prev) => ({
        ...prev,
        total: totalCount,
        // Reset to page 1 if current page would be out of bounds
        page: Math.max(
          1,
          Math.min(prev.page, Math.ceil(totalCount / prev.per_page) || 1)
        ),
      }));
    }

    return { paginatedData, totalCount };
  }, [summaryPreview, pagination.page, pagination.per_page, pagination.total]);

  // NEW: Open individual invoice preview modal
  const handleOpenPreviewModal = async (row) => {
    // For flat rate services, require milestone selection
    if (row.flat_rate) {
      const selectedMilestoneId = selectedMilestones[row.authorization_id];
      if (!selectedMilestoneId) {
        toast.warning("Please select a milestone to invoice for this flat rate service.");
        return;
      }
      
      // Verify the milestone is not already invoiced
      const milestones = milestonesCache.get(row.authorization_id) || [];
      const selectedMilestone = milestones.find(m => m.milestone_id === selectedMilestoneId);
      if (selectedMilestone?.is_invoiced) {
        toast.error("This milestone has already been invoiced. Please select a different milestone.");
        return;
      }
    }

    setLoadingPreview(true);

    try {
      secureAxios.defaults.headers.common[
        "X-Audit-Reason"
      ] = `Preview individual invoice for ${row.consumer_name}`;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      // Get the detailed logs for this authorization
      const details = loadInvoiceDetails(row.authorization_number);

      // Get selected milestone info for flat rate
      const selectedMilestoneId = selectedMilestones[row.authorization_id];
      const milestones = milestonesCache.get(row.authorization_id) || [];
      const selectedMilestone = milestones.find(m => m.milestone_id === selectedMilestoneId);

      // Build the preview data object for the modal
      const invoicePreviewData = {
        authorization_id: row.authorization_id,
        authorization_number: row.authorization_number,
        consumer_id: row.consumer_id,
        consumer_name: row.consumer_name,
        customer_id: row.customer_id,
        customer_name: row.customer_name,
        user_name: row.user_name,
        subtotal_services: row.total_hours * row.rate,
        subtotal_mileage:
          row.total_miles *
          (details.mileage_logs[0]?.applicable_rate ||
            details.mileage_logs[0]?.rate ||
            0),
        total_hours: row.total_hours,
        total_miles: row.total_miles,
        flat_rate: row.flat_rate,
        service_rate: row.rate,
        // Milestone info for flat rate invoicing
        milestone_id: selectedMilestoneId || null,
        milestone_name: selectedMilestone?.milestone_name || null,
        // Check if mileage logs have varying rates
        mileage_rate: (() => {
          const logs = details.mileage_logs || [];
          if (logs.length === 0) return 0;
          const rates = logs.map(
            (l) => parseFloat(l.applicable_rate) || parseFloat(l.rate) || 0
          );
          const uniqueRates = [...new Set(rates)];
          // If all rates are the same, return that rate; otherwise return null to indicate varying
          return uniqueRates.length === 1 ? uniqueRates[0] : null;
        })(),
        job_logs: details.job_logs || row.job_logs || [],
        mileage_logs: details.mileage_logs || row.mileage_logs || [],
      };

      // Calculate subtotals properly
      let subtotalServices = 0;
      let subtotalMileage = 0;
      let flatRateCalculated = false;

      (invoicePreviewData.job_logs || []).forEach((log) => {
        const hours = parseFloat(log.hours_worked) || 0;
        // Use applicable_rate (date-based) if available, fallback to rate
        const rate =
          parseFloat(log.applicable_rate) || parseFloat(log.rate) || 0;
        if (log.flat_rate) {
          // Flat rate: use just the rate (one flat fee per invoice)
          if (!flatRateCalculated) {
            subtotalServices += rate;
            flatRateCalculated = true;
          }
        } else {
          // Exact per-line — no rounding here
          subtotalServices += hours * rate;
        }
      });

      (invoicePreviewData.mileage_logs || []).forEach((log) => {
        const miles = parseFloat(log.total_miles) || 0;
        // Use applicable_rate (date-based) if available, fallback to rate
        const rate =
          parseFloat(log.applicable_rate) || parseFloat(log.rate) || 0;
        // Exact per-line — no rounding here
        subtotalMileage += miles * rate;
      });

      // Round the subtotals once — after all exact line amounts have been summed
      invoicePreviewData.subtotal_services = roundAmount(subtotalServices);
      invoicePreviewData.subtotal_mileage = roundAmount(subtotalMileage);
      invoicePreviewData.authorization_hours =
        invoicePreviewData.job_logs[0]?.authorization_hours || 0;

      await logAuditEvent({
        action: "OPEN_INVOICE_PREVIEW_MODAL",
        entity_type: "INVOICE",
        entity_id: null,
        entity_identifier: `Preview for ${row.consumer_name} - Auth: ${row.authorization_number}`,
        contains_phi: true,
        phi_types: ["consumer_name", "authorization_number"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });

      setPreviewInvoiceData(invoicePreviewData);
      setShowPreviewModal(true);
    } catch (err) {
      console.error("Failed to open preview modal:", err);
      toast.error("Failed to load invoice preview");
    } finally {
      setLoadingPreview(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  // NEW: Handle invoice generated from modal
  const handleInvoiceGenerated = async (invoiceData) => {
    await logAuditEvent({
      action: "GENERATE_INVOICE_WITH_TAX_SUCCESS",
      entity_type: "INVOICE",
      entity_id: invoiceData.invoice_id,
      entity_identifier: `Invoice ${invoiceData.invoice_number} for ${previewInvoiceData?.consumer_name}`,
      contains_phi: true,
      phi_types: ["consumer_name", "authorization_number", "total_amount"],
      purpose_of_use: PURPOSES.PAYMENT,
      action_status: "SUCCESS",
      new_values: {
        invoice_id: invoiceData.invoice_id,
        invoice_number: invoiceData.invoice_number,
        total_amount: invoiceData.total_amount,
      },
    });

    setShowPreviewModal(false);
    setPreviewInvoiceData(null);

    // Enrich the invoice data with info from the preview that the backend doesn't return
    const enrichedInvoiceData = {
      ...invoiceData,
      consumer_name:
        invoiceData.consumer_name || previewInvoiceData?.consumer_name,
      user_name: invoiceData.user_name || previewInvoiceData?.user_name,
      authorization_number:
        invoiceData.authorization_number ||
        previewInvoiceData?.authorization_number,
      created_at: invoiceData.created_at || new Date().toISOString(),
      invoice_date: invoiceData.invoice_date || new Date().toISOString(),
    };

    // Add the new invoice to the list
    setInvoices((prev) => [...prev, enrichedInvoiceData]);

    // Refresh the preview data
    await fetchFilteredPreview();

    toast.success(
      `Invoice ${invoiceData.invoice_number} generated successfully!`
    );

    // Open the PDF
    if (invoiceData.invoice_id) {
      window.open(
        `${apiBase}/api/invoices/${invoiceData.invoice_id}/pdf`,
        "_blank"
      );
    }
  };

  // Generate invoices (BATCH MODE - Original functionality)
  const handleGenerateInvoices = async () => {
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        "Generate invoices for billing";
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      await logAuditEvent({
        action: "GENERATE_INVOICE_ATTEMPT",
        entity_type: "INVOICE",
        entity_id: null,
        entity_identifier: `Generating invoices for ${
          filters.start_date || "all dates"
        } to ${filters.end_date || "all dates"}`,
        contains_phi: true,
        action_status: "ATTEMPT",
      });

      let res;
      try {
        res = await secureAxios.post(`${apiBase}/api/invoices/generate-batch`, {
          tenant_id,
          ...filters, // [OK] This spreads filters to top level
        });
      } catch (batchErr) {
        console.log("Batch endpoint not available, using original method");
        res = await secureAxios.post(`${apiBase}/api/invoices/generate`, {
          tenant_id,
          user_id,
          ...filters,
        });
      }

      const generated = Array.isArray(res.data.invoices)
        ? res.data.invoices
        : [];

      if (generated.length === 0) {
        setSuccessMessage("No invoices were generated.");
        setInvoices([]);
        return;
      }

      setSuccessMessage("Invoices successfully generated!");
      setInvoices(generated);

      for (const inv of generated) {
        await logAuditEvent({
          action: "GENERATE_INVOICE_SUCCESS",
          entity_type: "INVOICE",
          entity_id: inv.invoice_id,
          entity_identifier: `Invoice for ${inv.consumer_name} - Auth: ${inv.authorization_number}`,
          contains_phi: true,
          phi_types: ["consumer_name", "authorization_number", "total_amount"],
          purpose_of_use: PURPOSES.PAYMENT,
          action_status: "SUCCESS",
          new_values: {
            invoice_id: inv.invoice_id,
            total_amount: inv.total_amount,
            consumer_name: inv.consumer_name,
            authorization_number: inv.authorization_number,
          },
        });
      }

      if (generated.length > 0) {
        const firstPdfUrl = `${apiBase}/api/invoices/${generated[0].invoice_id}/pdf`;
        window.open(firstPdfUrl, "_blank");

        if (generated.length > 1) {
          toast.info(
            `${
              generated.length - 1
            } more invoice(s) generated. View them below.`
          );
        }
      }

      setFilters({
        start_date: "",
        end_date: "",
        consumer_id: "",
        user_id: "",
        auth_number: "",
      });

      // Refresh preview - reset initialLoadComplete to trigger a fresh load
      setInitialLoadComplete(false);
    } catch (err) {
      console.error("Failed to generate invoices", err);
      setError("Something went wrong while generating invoices.");
      toast.error("Failed to generate invoices");

      await logAuditEvent({
        action: "GENERATE_INVOICE_FAILED",
        entity_type: "INVOICE",
        entity_id: null,
        action_status: "FAILED",
        action_reason: err.message,
      });
    } finally {
      setLoading(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  // Email functionality
  const handleEmailInvoices = () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices to email");
      return;
    }
    setEmailModalVisible(true);
    setEmailSubjectValue(
      `Invoice Bundle - ${selectedInvoices.length} Invoice${
        selectedInvoices.length > 1 ? "s" : ""
      }`
    );
    setEmailBodyValue(
      `Please find attached ${selectedInvoices.length} invoice${
        selectedInvoices.length > 1 ? "s" : ""
      } for your review.`
    );
  };

  const sendEmailBundle = async () => {
    if (!emailToValue || !emailSubjectValue || !emailBodyValue) {
      toast.error("Please fill in all email fields");
      return;
    }

    try {
      const invoice_ids = selectedInvoices
        .map((authNumber) => {
          const matchingInvoice = invoices.find(
            (inv) => inv.authorization_number === authNumber
          );
          return matchingInvoice?.invoice_id;
        })
        .filter(Boolean);

      if (invoice_ids.length === 0) {
        toast.error("No valid invoice IDs found for selected invoices");
        return;
      }

      const response = await secureAxios.post(
        `${apiBase}/api/invoices/email-bundle`,
        {
          invoice_ids,
          to_email: emailToValue,
          subject: emailSubjectValue,
          body: emailBodyValue,
          tenant_id,
        }
      );

      if (response.data?.success) {
        toast.success("Email sent successfully!");
        setEmailModalVisible(false);
        setSelectedInvoices([]);
      }
    } catch (err) {
      console.error("Failed to send email:", err);
      toast.error("Failed to send email");
    }
  };

  const handleInvoiceSelection = (authorizationNumber) => {
    setSelectedInvoices((prev) =>
      prev.includes(authorizationNumber)
        ? prev.filter((auth) => auth !== authorizationNumber)
        : [...prev, authorizationNumber]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    // Clear any previously generated invoices when applying new filters
    setInvoices([]);
    setError(null);
    setSuccessMessage("");
    fetchFilteredPreview();
  };

  // Dedicated refresh function to reload preview data
  const handleRefreshPreview = () => {
    // Clear generated invoices and reload data
    setInvoices([]);
    setError(null);
    setSuccessMessage("");
    setExpandedRows(new Set());
    setDetailsCache(new Map());
    fetchFilteredPreview();
    toast.info("Refreshing invoice data...");
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      consumer_id: "",
      user_id: "",
      auth_number: "",
    });
    // Reset and reload
    setInitialLoadComplete(false);
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "-";

    // Handle different date formats
    if (typeof dateString === "string") {
      // For ISO date strings like "YYYY-MM-DD"
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Create date object but add timezone offset to prevent shifting
        const date = new Date(dateString + "T00:00:00");
        return date.toLocaleDateString();
      }

      // For ISO datetime strings like "2025-07-30T00:00:00Z"
      if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
        // Extract just the date part to avoid timezone issues
        const datePart = dateString.split("T")[0];
        const [year, month, day] = datePart.split("-");
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString();
      }
    }

    // Fallback for other formats
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString();
    } catch (error) {
      return "-";
    }
  };

  return (
    <div className="container-fluid py-3 py-md-4">
      <SessionMonitor />
      
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 gap-2">
        <h3 className="mb-0 h4 h3-md">
          <i className="bi bi-receipt me-2"></i>
          Generate Invoices
        </h3>
      </div>

      {/* HIPAA Notice */}
      <div className="alert alert-warning mb-3 py-2">
        <div className="d-flex align-items-start">
          <i className="bi bi-shield-lock me-2 mt-1 flex-shrink-0"></i>
          <div>
            <strong>PHI Notice:</strong>
            <span className="d-none d-sm-inline">
              {" "}This page displays consumer names, authorization numbers, and
              service details. All access is logged for HIPAA compliance.
            </span>
            <span className="d-sm-none"> Access logged for HIPAA compliance.</span>
          </div>
        </div>
      </div>

      {/* Monthly Report Status Warning */}
      {hasIncompleteReports && (
        <div className="alert alert-info mb-3 py-2">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Notice:</strong>
          <span className="d-none d-sm-inline">
            {" "}Some authorizations have incomplete or missing monthly reports.
            While you can still generate invoices, please ensure all monthly
            reports are completed for proper documentation.
          </span>
          <span className="d-sm-none"> Some monthly reports incomplete.</span>
        </div>
      )}

      {/* Filters - Collapsible on Mobile */}
      <div className="card mb-3">
        <div
          className="card-header d-flex justify-content-between align-items-center d-md-none py-2"
          onClick={() => setShowFilters(!showFilters)}
          style={{ cursor: "pointer" }}
        >
          <span>
            <i className="bi bi-funnel me-2"></i>
            Filters
            {(filters.start_date || filters.end_date || filters.consumer_id || filters.user_id || filters.auth_number) && (
              <span className="badge bg-primary ms-2">
                {[filters.start_date, filters.end_date, filters.consumer_id, filters.user_id, filters.auth_number].filter(Boolean).length}
              </span>
            )}
          </span>
          <i className={`bi bi-chevron-${showFilters ? "up" : "down"}`}></i>
        </div>
        <div className={`card-body p-2 p-md-3 ${showFilters ? "" : "d-none d-md-block"}`}>
          <div className="row g-2 g-md-3">
            <div className="col-6 col-md-3">
              <label className="form-label small">Start Date</label>
              <input
                type="date"
                name="start_date"
                className="form-control form-control-sm"
                value={filters.start_date}
                onChange={handleChange}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small">End Date</label>
              <input
                type="date"
                name="end_date"
                className="form-control form-control-sm"
                value={filters.end_date}
                onChange={handleChange}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small">Consumer</label>
              <select
                className="form-select form-select-sm"
                name="consumer_id"
                value={filters.consumer_id}
                onChange={handleChange}
              >
                <option value="">All Consumers</option>
                {consumers.map((c) => (
                  <option key={c.consumer_id} value={c.consumer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small">Job Coach</label>
              <select
                className="form-select form-select-sm"
                name="user_id"
                value={filters.user_id}
                onChange={handleChange}
              >
                <option value="">All Coaches</option>
                {coaches.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small">Auth #</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={filters.auth_number}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, auth_number: e.target.value }))
                }
                placeholder="Search..."
              />
            </div>
            <div className="col-12 col-md-9 d-flex align-items-end gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={handleApplyFilters}
                disabled={previewLoading}
              >
                <i className="bi bi-search me-1 d-none d-sm-inline"></i>
                Apply
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="alert alert-success py-2" role="alert">
          {successMessage}
        </div>
      )}

      {/* Generation Mode Toggle - Only show if invoice enhancements enabled */}
      {invoiceEnhancementsEnabled && (
        <div className="card p-2 p-md-3 mb-3">
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3">
            <span className="fw-bold small">Mode:</span>
            <div className="btn-group btn-group-sm" role="group">
              <input
                type="radio"
                className="btn-check"
                name="generationMode"
                id="modeBatch"
                checked={generationMode === "batch"}
                onChange={() => setGenerationMode("batch")}
              />
              <label className="btn btn-outline-primary" htmlFor="modeBatch">
                <i className="bi bi-collection me-1"></i>
                <span className="d-none d-sm-inline">Batch (All at Once)</span>
                <span className="d-sm-none">Batch</span>
              </label>

              <input
                type="radio"
                className="btn-check"
                name="generationMode"
                id="modeIndividual"
                checked={generationMode === "individual"}
                onChange={() => setGenerationMode("individual")}
              />
              <label
                className="btn btn-outline-primary"
                htmlFor="modeIndividual"
              >
                <i className="bi bi-receipt me-1"></i>
                <span className="d-none d-sm-inline">Individual (With Tax)</span>
                <span className="d-sm-none">Single</span>
              </label>
            </div>
            <small className="text-muted d-none d-md-inline">
              {generationMode === "batch"
                ? "Generate all invoices at once without tax adjustments"
                : "Click on each row to preview and add tax before generating"}
            </small>
          </div>
        </div>
      )}

      {/* Action Buttons - Only show for Batch Mode */}
      {generationMode === "batch" && (
        <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <button
            className="btn btn-primary"
            onClick={handleGenerateInvoices}
            disabled={
              loading || summaryPreview.length === 0 || invoices.length > 0
            }
            style={{
              opacity: invoices.length > 0 ? 0.5 : 1,
              cursor: invoices.length > 0 ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                <span className="d-none d-sm-inline">Generating...</span>
                <span className="d-sm-none">...</span>
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-text me-1 me-sm-2"></i>
                {invoices.length > 0 ? (
                  <>
                    <span className="d-none d-sm-inline">Generated </span>
                    {invoices.length}
                    <span className="d-none d-sm-inline"> Invoice{invoices.length !== 1 ? "s" : ""}</span>
                  </>
                ) : (
                  <>
                    <span className="d-none d-sm-inline">Generate </span>
                    {summaryPreview.length > 0
                      ? `${summaryPreview.length} `
                      : ""}
                    <span className="d-none d-sm-inline">Invoices</span>
                    <span className="d-sm-none">Inv</span>
                  </>
                )}
              </>
            )}
          </button>

          {/* Refresh Button */}
          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshPreview}
            disabled={loading || previewLoading}
            title="Refresh invoice data"
          >
            {previewLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise"></i>
            )}
            <span className="d-none d-sm-inline ms-1">Refresh</span>
          </button>

          {/* Only show "Ready to generate" message if no invoices have been generated yet */}
          {summaryPreview.length > 0 && invoices.length === 0 && (
            <div className="d-none d-sm-flex align-items-center text-muted small">
              <i className="bi bi-info-circle me-2"></i>
              Ready to generate {summaryPreview.length} invoice
              {summaryPreview.length !== 1 ? "s" : ""}
            </div>
          )}

          {/* Show completion message after invoices are generated */}
          {invoices.length > 0 && (
            <div className="d-flex align-items-center text-success small">
              <i className="bi bi-check-circle-fill me-2"></i>
              <span className="d-none d-sm-inline">Successfully generated </span>
              {invoices.length}
              <span className="d-none d-sm-inline"> invoice{invoices.length !== 1 ? "s" : ""}</span>
            </div>
          )}
        </div>
      )}

      {/* Individual Mode Instructions */}
      {generationMode === "individual" && (
        <div className="alert alert-primary mb-3 py-2 small">
          <i className="bi bi-info-circle me-2"></i>
          <span className="d-none d-sm-inline">
            <strong>Individual Mode:</strong> Click the{" "}
            <i className="bi bi-receipt"></i> button on any row to open the
            invoice preview. You can review details, adjust tax amounts, and
            generate the invoice with a sequential invoice number.
          </span>
          <span className="d-sm-none">
            Tap <i className="bi bi-receipt"></i> to preview with tax options.
          </span>
        </div>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* Generated Invoices - Desktop Table / Mobile Cards */}
      {invoices.length > 0 && (
        <div className="card mb-3">
          <div className="card-header py-2">
            <h5 className="mb-0 h6">
              <i className="bi bi-check-circle me-2 text-success"></i>
              {invoices.length} Invoice{invoices.length !== 1 ? "s" : ""} Created
            </h5>
          </div>
          <div className="card-body p-0">
            {/* Desktop Table */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-bordered mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Invoice #</th>
                    <th>Authorization</th>
                    <th>Consumer</th>
                    <th>Job Coach</th>
                    <th className="text-end">Amount</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.invoice_id}>
                      <td>
                        <span className="fw-bold">
                          {inv.invoice_number || (
                            <code className="small">{inv.invoice_id}</code>
                          )}
                        </span>
                      </td>
                      <td>{inv.authorization_number || "-"}</td>
                      <td>
                        <i className="bi bi-person-fill me-1 text-muted"></i>
                        {inv.consumer_name || "-"}
                      </td>
                      <td>{inv.user_name || inv.job_coach || "-"}</td>
                      <td className="text-end">
                        {isNaN(Number(inv.total_amount))
                          ? "-"
                          : `$${Number(inv.total_amount).toFixed(2)}`}
                      </td>
                      <td>
                        {inv.created_at && !isNaN(new Date(inv.created_at))
                          ? new Date(inv.created_at).toLocaleDateString()
                          : inv.invoice_date && !isNaN(new Date(inv.invoice_date))
                          ? new Date(inv.invoice_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => {
                            const pdfUrl = `${apiBase}/api/invoices/${inv.invoice_id}/pdf`;
                            window.open(pdfUrl, "_blank");
                          }}
                        >
                          <i className="bi bi-file-pdf me-1"></i>
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {invoices.map((inv) => (
                <div key={inv.invoice_id} className="border-bottom p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="fw-bold">
                        {inv.invoice_number || inv.invoice_id}
                      </div>
                      <small className="text-muted">
                        {inv.authorization_number}
                      </small>
                    </div>
                    <span className="badge bg-success">
                      ${Number(inv.total_amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="small mb-2">
                    <i className="bi bi-person me-1"></i>
                    {inv.consumer_name || "-"}
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {inv.created_at
                        ? new Date(inv.created_at).toLocaleDateString()
                        : "-"}
                    </small>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        window.open(
                          `${apiBase}/api/invoices/${inv.invoice_id}/pdf`,
                          "_blank"
                        );
                      }}
                    >
                      <i className="bi bi-file-pdf me-1"></i>
                      View PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card-footer small text-muted py-2">
            <i className="bi bi-info-circle me-1"></i>
            <span className="d-none d-sm-inline">
              {invoices.length === 1
                ? "PDF has been opened in a new tab."
                : `${invoices.length} invoices generated. The first PDF was opened automatically.`}
              {" "}All activities logged for audit compliance.{" "}
              <a href="/crm/posted-invoices" className="text-primary">
                View all posted invoices
              </a>
            </span>
            <span className="d-sm-none">
              PDF opened.{" "}
              <a href="/crm/posted-invoices" className="text-primary">View Posted Invoices</a>
            </span>
          </div>
        </div>
      )}

      {/* Enhanced Preview Section */}
      {previewLoading ? (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading preview...</span>
          </div>
          <div className="mt-2 text-muted small">Loading preview...</div>
        </div>
      ) : summaryPreview.length > 0 ? (
        <div className="card mb-3">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 h6">
              <i className="bi bi-eye me-2"></i>
              <span className="d-none d-sm-inline">Invoice </span>Preview
              <span className="badge bg-secondary ms-2">{summaryPreview.length}</span>
            </h5>
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="alert alert-info py-2 small mb-2">
              <i className="bi bi-shield-check me-1"></i>
              <span className="d-none d-sm-inline">
                This preview contains PHI. Access has been logged.
                {generationMode === "individual"
                  ? " Click the invoice icon to preview with tax inputs."
                  : " Click the arrows to view detailed logs."}
              </span>
              <span className="d-sm-none">PHI access logged.</span>
            </div>

            {/* Desktop Table View */}
            <div className="table-responsive d-none d-lg-block">
            <table className="table table-sm table-striped mt-2">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>Authorization</th>
                  <th>Consumer</th>
                  <th>Job Coach</th>
                  <th className="text-end">Total Miles</th>
                  <th className="text-end">Total Hours</th>
                  <th className="text-end">Total Amount</th>
                  <th className="text-center">Flat Rate?</th>
                  {generationMode === "individual" && (
                    <th className="text-center" style={{ width: "100px" }}>
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {getPaginatedPreview().paginatedData.map((row) => {
                  const isExpanded = expandedRows.has(row.authorization_number);
                  const details = detailsCache.get(row.authorization_number);

                  return (
                    <React.Fragment key={row.authorization_number}>
                      <tr>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              toggleRowExpansion(row.authorization_number)
                            }
                            title="View details"
                          >
                            <i
                              className={`bi bi-chevron-${
                                isExpanded ? "down" : "right"
                              }`}
                            ></i>
                          </button>
                        </td>
                        <td>{row.authorization_number}</td>
                        <td>
                          <i className="bi bi-person-fill me-1 text-muted"></i>
                          {row.consumer_name}
                        </td>
                        <td>{row.user_name}</td>
                        <td className="text-end">
                          {parseFloat(row.total_miles || 0).toFixed(1)}
                        </td>
                        <td className="text-end">
                          {parseFloat(row.total_hours || 0).toFixed(2)}
                        </td>
                        <td
                          className="text-end"
                          style={{
                            fontWeight: row.flat_rate ? "bold" : "normal",
                            color: row.flat_rate ? "#198754" : "inherit",
                          }}
                        >
                          $
                          {roundAmount(parseFloat(row.total_amount || 0))
                            .toFixed(2)
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className="text-center">
                          {row.flat_rate ? (
                            <span className="badge bg-success">Yes</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                        {generationMode === "individual" && (
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleOpenPreviewModal(row)}
                              disabled={loadingPreview}
                              title="Preview & Generate with Tax"
                            >
                              {loadingPreview ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <>
                                  <i className="bi bi-receipt me-1"></i>
                                  Invoice
                                </>
                              )}
                            </button>
                          </td>
                        )}
                      </tr>

                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={
                              generationMode === "individual" ? "9" : "8"
                            }
                            className="bg-light"
                          >
                            <div className="p-3">
                              <h6>
                                Detailed Logs for {row.authorization_number}
                              </h6>

                              {details?.job_logs &&
                                details.job_logs.length > 0 && (
                                  <div className="mb-3">
                                    <h6 className="text-primary">
                                      Job Logs ({details.job_logs.length})
                                      {row.flat_rate && (
                                        <span className="badge bg-success ms-2">
                                          Flat Rate
                                        </span>
                                      )}
                                    </h6>
                                    {row.flat_rate && (
                                      <div className="alert alert-info py-2 small mb-2">
                                        <i className="bi bi-info-circle me-1"></i>
                                        <strong>Flat Rate Service:</strong>{" "}
                                        Invoice amount is the flat rate of {formatRate(row.rate)}
                                        , regardless of hours logged.
                                        ({row.authorization_hours || 0} total units authorized)
                                      </div>
                                    )}
                                    {/* Milestone Selection for Flat Rate */}
                                    {row.flat_rate && (
                                      <MilestoneSelector
                                        authorizationId={row.authorization_id}
                                        milestonesCache={milestonesCache}
                                        loadMilestones={loadMilestones}
                                        selectedMilestones={selectedMilestones}
                                        setSelectedMilestones={setSelectedMilestones}
                                        rate={row.rate}
                                      />
                                    )}
                                    <div className="table-responsive">
                                      <table className="table table-sm">
                                        <thead>
                                          <tr>
                                            <th>Date</th>
                                            <th>Service</th>
                                            <th>Hours</th>
                                            <th>Rate</th>
                                            <th>
                                              Amount
                                              {row.flat_rate
                                                ? " (Reference)"
                                                : ""}
                                            </th>
                                            <th>Monthly Report</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details.job_logs.map((log) => {
                                            const { month, year } =
                                              extractDateParts(
                                                log.date_of_service
                                              );
                                            const key = `${row.authorization_number}_${month}_${year}`;
                                            const status =
                                              monthlyReportStatuses[key];

                                            return (
                                              <tr key={log.job_log_id}>
                                                <td>
                                                  {formatDateForDisplay(
                                                    log.date_of_service
                                                  )}
                                                </td>
                                                <td>{log.service_name}</td>
                                                <td>{log.hours_worked}</td>
                                                <td
                                                  style={
                                                    row.flat_rate
                                                      ? {
                                                          textDecoration:
                                                            "line-through",
                                                          color: "#6c757d",
                                                        }
                                                      : {}
                                                  }
                                                >
                                                  {formatRate(
                                                    log.applicable_rate ||
                                                    log.rate
                                                  )}
                                                </td>
                                                <td
                                                  style={
                                                    row.flat_rate
                                                      ? {
                                                          textDecoration:
                                                            "line-through",
                                                          color: "#6c757d",
                                                        }
                                                      : {}
                                                  }
                                                >
                                                  $
                                                  {formatRate(
                                                    log.hours_worked *
                                                    (log.applicable_rate ||
                                                      log.rate)
                                                  ).replace("$", "")}
                                                </td>
                                                <td className="text-center">
                                                  {status?.hasMatchingReport &&
                                                  status?.status ===
                                                    "Completed" ? (
                                                    <i
                                                      className="bi bi-check-circle-fill text-success"
                                                      title={`Monthly report completed for ${month}/${year}`}
                                                    ></i>
                                                  ) : status?.hasMatchingReport ? (
                                                    <i
                                                      className="bi bi-exclamation-circle-fill text-warning"
                                                      title={`Monthly report pending for ${month}/${year}`}
                                                    ></i>
                                                  ) : (
                                                    <i
                                                      className="bi bi-x-circle-fill text-danger"
                                                      title={`No monthly report for ${month}/${year}`}
                                                    ></i>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                              {details?.mileage_logs &&
                                details.mileage_logs.length > 0 && (
                                  <div>
                                    <h6 className="text-success">
                                      Mileage Logs (
                                      {details.mileage_logs.length})
                                    </h6>
                                    <div className="table-responsive">
                                      <table className="table table-sm">
                                        <thead>
                                          <tr>
                                            <th>Date</th>
                                            <th>Service</th>
                                            <th>Miles</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                            <th>Monthly Report</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details.mileage_logs.map((log) => {
                                            const { month, year } =
                                              extractDateParts(log.trip_date);
                                            const key = `${row.authorization_number}_${month}_${year}`;
                                            const status =
                                              monthlyReportStatuses[key];

                                            return (
                                              <tr key={log.log_id}>
                                                <td>
                                                  {formatDateForDisplay(
                                                    log.trip_date
                                                  )}
                                                </td>
                                                <td>{log.service_name}</td>
                                                <td>{log.total_miles}</td>
                                                <td>
                                                  {formatRate(
                                                    log.applicable_rate ||
                                                    log.rate
                                                  )}
                                                </td>
                                                <td>
                                                  $
                                                  {formatRate(
                                                    log.total_miles *
                                                    (log.applicable_rate ||
                                                      log.rate)
                                                  ).replace("$", "")}
                                                </td>
                                                <td className="text-center">
                                                  <i
                                                    className="bi bi-dash-circle text-secondary"
                                                    title="Monthly report not required for mileage"
                                                  ></i>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                              {(!details ||
                                (details.job_logs.length === 0 &&
                                  details.mileage_logs.length === 0)) && (
                                <div className="text-muted">
                                  No detailed logs found for this authorization.
                                </div>
                              )}

                              {/* Monthly Report Status Legend */}
                              <div className="mt-3 small text-muted d-none d-md-block">
                                <i className="bi bi-info-circle me-1"></i>
                                Monthly Report Status:
                                <i className="bi bi-check-circle-fill text-success ms-2 me-1"></i>
                                Complete
                                <i className="bi bi-exclamation-circle-fill text-warning ms-2 me-1"></i>
                                Pending
                                <i className="bi bi-x-circle-fill text-danger ms-2 me-1"></i>
                                No Report
                                <i className="bi bi-dash-circle text-secondary ms-2 me-1"></i>
                                Not Needed
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="d-block d-lg-none">
              {getPaginatedPreview().paginatedData.map((row) => {
                const isExpanded = expandedRows.has(row.authorization_number);
                const details = detailsCache.get(row.authorization_number);

                return (
                  <div key={row.authorization_number} className="card mb-2">
                    <div className="card-body p-2">
                      {/* Header Row */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="fw-bold small text-truncate">
                            {row.authorization_number}
                          </div>
                          <div className="small text-muted text-truncate">
                            <i className="bi bi-person me-1"></i>
                            {row.consumer_name}
                          </div>
                        </div>
                        <div className="text-end ms-2">
                          <div
                            className="fw-bold"
                            style={{
                              color: row.flat_rate ? "#198754" : "inherit",
                            }}
                          >
                            ${roundAmount(parseFloat(row.total_amount || 0)).toFixed(2)}
                          </div>
                          {row.flat_rate && (
                            <span className="badge bg-success" style={{ fontSize: "0.65rem" }}>
                              Flat
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="d-flex gap-3 small text-muted mb-2">
                        <span>
                          <i className="bi bi-clock me-1"></i>
                          {parseFloat(row.total_hours || 0).toFixed(1)}h
                        </span>
                        <span>
                          <i className="bi bi-signpost me-1"></i>
                          {parseFloat(row.total_miles || 0).toFixed(0)}mi
                        </span>
                        <span className="text-truncate">{row.user_name}</span>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary flex-grow-1"
                          onClick={() => toggleRowExpansion(row.authorization_number)}
                        >
                          <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} me-1`}></i>
                          {isExpanded ? "Hide" : "Details"}
                        </button>
                        {generationMode === "individual" && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleOpenPreviewModal(row)}
                            disabled={loadingPreview}
                          >
                            <i className="bi bi-receipt me-1"></i>
                            Invoice
                          </button>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-3 pt-2 border-top">
                          {/* Milestone Selector for Flat Rate (Mobile) */}
                          {row.flat_rate && (
                            <div className="mb-3">
                              <MilestoneSelector
                                authorizationId={row.authorization_id}
                                milestonesCache={milestonesCache}
                                loadMilestones={loadMilestones}
                                selectedMilestones={selectedMilestones}
                                setSelectedMilestones={setSelectedMilestones}
                                rate={row.rate}
                              />
                            </div>
                          )}
                          {details?.job_logs && details.job_logs.length > 0 && (
                            <div className="mb-2">
                              <div className="small fw-bold text-primary mb-1">
                                Job Logs ({details.job_logs.length})
                                {row.flat_rate && (
                                  <span className="badge bg-success ms-1" style={{ fontSize: "0.6rem" }}>Flat Rate</span>
                                )}
                              </div>
                              {details.job_logs.map((log) => (
                                <div
                                  key={log.job_log_id}
                                  className="d-flex justify-content-between small border-bottom py-1"
                                >
                                  <span>{formatDateForDisplay(log.date_of_service)}</span>
                                  <span>{log.hours_worked}h</span>
                                  <span>
                                    ${formatRate(
                                      log.hours_worked *
                                      (log.applicable_rate || log.rate)
                                    ).replace("$", "")}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {details?.mileage_logs && details.mileage_logs.length > 0 && (
                            <div>
                              <div className="small fw-bold text-success mb-1">
                                Mileage ({details.mileage_logs.length})
                              </div>
                              {details.mileage_logs.map((log) => (
                                <div
                                  key={log.log_id}
                                  className="d-flex justify-content-between small border-bottom py-1"
                                >
                                  <span>{formatDateForDisplay(log.trip_date)}</span>
                                  <span>{log.total_miles}mi</span>
                                  <span>
                                    ${formatRate(
                                      log.total_miles *
                                      (log.applicable_rate || log.rate)
                                    ).replace("$", "")}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {summaryPreview.length > 10 && (
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3 pt-2 border-top">
                <small className="text-muted">
                  {(pagination.page - 1) * pagination.per_page + 1}-
                  {Math.min(
                    pagination.page * pagination.per_page,
                    summaryPreview.length
                  )}{" "}
                  of {summaryPreview.length}
                </small>
                <div className="d-flex align-items-center gap-2">
                  <select
                    value={pagination.per_page}
                    onChange={(e) =>
                      setPagination({
                        ...pagination,
                        per_page: parseInt(e.target.value),
                        page: 1,
                      })
                    }
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${
                          pagination.page === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            setPagination({
                              ...pagination,
                              page: Math.max(1, pagination.page - 1),
                            })
                          }
                          disabled={pagination.page === 1}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>
                      <li className="page-item active">
                        <span className="page-link text-nowrap">
                          {pagination.page}/
                          {Math.ceil(
                            summaryPreview.length / pagination.per_page
                          ) || 1}
                        </span>
                      </li>
                      <li
                        className={`page-item ${
                          pagination.page * pagination.per_page >=
                          summaryPreview.length
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            setPagination({
                              ...pagination,
                              page: pagination.page + 1,
                            })
                          }
                          disabled={
                            pagination.page * pagination.per_page >=
                            summaryPreview.length
                          }
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="alert alert-info mb-3 py-2">
          <i className="bi bi-info-circle me-2"></i>
          No records to preview.{" "}
          {initialLoadComplete
            ? "All eligible logs may already be invoiced."
            : "Loading..."}
        </div>
      )}

      {/* Email Modal */}
      {emailModalVisible && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h5 className="modal-title h6">Email Invoice Bundle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEmailModalVisible(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small">To Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={emailToValue}
                    onChange={(e) => setEmailToValue(e.target.value)}
                    placeholder="recipient@email.com"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small">Subject *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={emailSubjectValue}
                    onChange={(e) => setEmailSubjectValue(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small">Message *</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={emailBodyValue}
                    onChange={(e) => setEmailBodyValue(e.target.value)}
                    required
                  ></textarea>
                </div>
                <div className="alert alert-light py-2 small">
                  <strong>{selectedInvoices.length}</strong> invoice
                  {selectedInvoices.length !== 1 ? "s" : ""} selected
                </div>
              </div>
              <div className="modal-footer py-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEmailModalVisible(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={sendEmailBundle}
                  disabled={
                    !emailToValue || !emailSubjectValue || !emailBodyValue
                  }
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Invoice Preview Modal with Tax Inputs - Only load when enhancements enabled */}
      {invoiceEnhancementsEnabled && (
        <Suspense
          fallback={
            <div className="text-center p-3">
              <span className="spinner-border"></span>
            </div>
          }
        >
          <InvoicePreviewModal
            show={showPreviewModal}
            onClose={() => {
              setShowPreviewModal(false);
              setPreviewInvoiceData(null);
            }}
            onGenerate={handleInvoiceGenerated}
            invoiceData={previewInvoiceData}
          />
        </Suspense>
      )}

      {/* Footer */}
      <div className="mt-3 mt-md-4 text-center text-muted small">
        <i className="bi bi-shield-check me-1"></i>
        <span className="d-none d-sm-inline">
          All activities on this page are logged for HIPAA compliance. User:{" "}
          {user?.email} | Tenant: {user?.tenant_name}
        </span>
        <span className="d-sm-none">HIPAA compliant</span>
      </div>
    </div>
  );
};

export default InvoiceGeneratorPage;